// Static-phase sample facts and client-side aggregation. Synthetic counts (not WES data) generated over the real location list; removed at integration, when the backend returns counts and ratios.
import type { AdminLocation } from '../../../../store/slices/adminLocationsSlice';
import type { WesFilterSelection, WesHierarchyLevel, WesTimeGrain } from './useWesPalletStagingFilters';
import { WES_WEEKS_PER_PERIOD } from './wesPalletStagingStaticData';

export interface WesStaticRow {
  locationId: string;
  locationName: string;
  regionName: string;
  marketName: string;
  year: number;
  period: number;
  // Week within the period, and week within the year.
  weekInPeriod: number;
  week: number;
  total: number;
  audit: number;
  other: number;
}

const UNASSIGNED = 'Unassigned';

// Previous year in full, current year up to the current week (default 23 when the current week is unknown).
export const buildStaticRows = (
  locations: AdminLocation[],
  current: { year: number; period: number; week: number } | null,
): WesStaticRow[] => {
  const currentYear = current?.year ?? new Date().getFullYear();
  const currentWeekInYear = current ? (current.period - 1) * WES_WEEKS_PER_PERIOD + current.week : 23;
  const years = [
    { year: currentYear - 1, weeks: 52 },
    { year: currentYear, weeks: currentWeekInYear },
  ];
  const sorted = [...locations].sort((a, b) => a.locName.localeCompare(b.locName));
  const rows: WesStaticRow[] = [];
  sorted.forEach((loc, li) => {
    years.forEach(({ year, weeks }) => {
      for (let week = 1; week <= weeks; week += 1) {
        // Missing weeks create gaps in the trend.
        if ((li * 7 + week) % 29 === 0) continue;
        const total = li === 29 && week % 5 === 0 ? 0 : 140 + ((li * 53 + week * 31 + year) % 160);
        const staged = Math.round(total * (0.55 + ((li * 17 + week * 13) % 40) / 100));
        const audit = Math.round(staged * (0.25 + ((li + week) % 20) / 100));
        const period = Math.ceil(week / WES_WEEKS_PER_PERIOD);
        rows.push({
          locationId: loc.locId,
          locationName: loc.locName,
          regionName: loc.region?.trim() || UNASSIGNED,
          marketName: loc.market?.trim() || UNASSIGNED,
          year,
          period,
          weekInPeriod: week - (period - 1) * WES_WEEKS_PER_PERIOD,
          week,
          total,
          audit,
          other: staged - audit,
        });
      }
    });
  });
  return rows;
};

const matches = (ids: string[], value: string) => ids.length === 0 || ids.includes(value);

// Filter ids use the same formats as the Crewing dropdowns: region/market names, locId, 'P5', 'P5W2'.
export const rowsInScope = (applied: WesFilterSelection, rows: WesStaticRow[]): WesStaticRow[] =>
  rows.filter(
    (r) =>
      matches(applied.regions, r.regionName) &&
      matches(applied.markets, r.marketName) &&
      matches(applied.locations, r.locationId) &&
      matches(applied.years, String(r.year)) &&
      matches(applied.periods, `P${r.period}`) &&
      matches(applied.weeks, `P${r.period}W${r.weekInPeriod}`),
  );

// ─── Aggregation ────────────────────────────────────────────────────────────

export type WesStagingScope = 'all' | 'audit' | 'other';

export interface WesSums {
  total: number;
  audit: number;
  other: number;
}

export const sumRows = (rows: WesStaticRow[]): WesSums =>
  rows.reduce((acc, r) => ({ total: acc.total + r.total, audit: acc.audit + r.audit, other: acc.other + r.other }), {
    total: 0,
    audit: 0,
    other: 0,
  });

export const stagedFor = (sums: WesSums, scope: WesStagingScope) =>
  scope === 'audit' ? sums.audit : scope === 'other' ? sums.other : sums.audit + sums.other;

export const notStaged = (sums: WesSums) => sums.total - sums.audit - sums.other;

export const ratio = (numerator: number, denominator: number): number | null =>
  denominator > 0 ? numerator / denominator : null;

// Provisional display helpers: the story's rounding and abbreviation rules are not recorded centrally.
export const formatPercent = (numerator: number, denominator: number): string => {
  const value = ratio(numerator, denominator);
  return value === null ? '-' : `${(Math.round(value * 1000) / 10).toFixed(1)}%`;
};

export const formatCount = (value: number): string => value.toLocaleString('en-US');

export const levelLabel = (row: WesStaticRow, level: WesHierarchyLevel) =>
  level === 'region' ? row.regionName : level === 'market' ? row.marketName : row.locationName;

export interface WesRankedGroup {
  label: string;
  sums: WesSums;
  value: number;
}

// Groups with no pallets are excluded from rankings. Ties fall back to label order (provisional).
export const rankGroups = (rows: WesStaticRow[], level: WesHierarchyLevel, scope: WesStagingScope): WesRankedGroup[] => {
  const byLabel = new Map<string, WesStaticRow[]>();
  rows.forEach((r) => {
    const label = levelLabel(r, level);
    byLabel.set(label, [...(byLabel.get(label) ?? []), r]);
  });
  const groups: WesRankedGroup[] = [];
  byLabel.forEach((groupRows, label) => {
    const sums = sumRows(groupRows);
    const value = ratio(stagedFor(sums, scope), sums.total);
    if (value !== null) groups.push({ label, sums, value });
  });
  return groups.sort((a, b) => b.value - a.value || a.label.localeCompare(b.label));
};

export interface WesBucket {
  key: string;
  label: string;
  order: number;
}

export const bucketOf = (row: WesStaticRow, grain: WesTimeGrain): WesBucket => {
  if (grain === 'year') return { key: `${row.year}`, label: `${row.year}`, order: row.year * 1000 };
  if (grain === 'period') return { key: `${row.year}-P${row.period}`, label: `${row.year} P${row.period}`, order: row.year * 1000 + row.period };
  return { key: `${row.year}-W${row.week}`, label: `${row.year} W${row.week}`, order: row.year * 1000 + row.week };
};

export interface WesTrendSeries {
  label: string;
  // null where the group has no pallets in that bucket: rendered as a gap.
  values: (number | null)[];
  // Staged (under the scope) and total pallets behind each value, for the point tooltip; null where the value is.
  counts: ({ staged: number; total: number } | null)[];
}

export interface WesTrend {
  buckets: WesBucket[];
  series: WesTrendSeries[];
}

export const buildTrend = (
  rows: WesStaticRow[],
  level: WesHierarchyLevel,
  grain: WesTimeGrain,
  scope: WesStagingScope,
): WesTrend => {
  const bucketMap = new Map<string, WesBucket>();
  const cells = new Map<string, Map<string, WesStaticRow[]>>();
  rows.forEach((r) => {
    const bucket = bucketOf(r, grain);
    bucketMap.set(bucket.key, bucket);
    const label = levelLabel(r, level);
    const byBucket = cells.get(label) ?? new Map<string, WesStaticRow[]>();
    byBucket.set(bucket.key, [...(byBucket.get(bucket.key) ?? []), r]);
    cells.set(label, byBucket);
  });
  const buckets = [...bucketMap.values()].sort((a, b) => a.order - b.order);

  const labels = [...cells.keys()].sort((a, b) => a.localeCompare(b));
  const series = labels.map((label) => {
    const cellSums = buckets.map((bucket) => sumRows(cells.get(label)?.get(bucket.key) ?? []));
    const values = cellSums.map((cell) => {
      const value = ratio(stagedFor(cell, scope), cell.total);
      return value === null ? null : value * 100;
    });
    const counts = cellSums.map((cell) => (cell.total > 0 ? { staged: stagedFor(cell, scope), total: cell.total } : null));
    return { label, values, counts };
  });
  return { buckets, series };
};
